export namespace Globals {

    export class Functions {

        /**
         * Creates a stable, three-character base-36 checksum for a fleet and call-centre ID.
         * This is intended to detect casual URL tampering, not as authorization.
         */
        public static createFleetCvv(
            fleetId: string | number,
            callcentreId: string | number
        ): string {
            const checksumInput = `fleet:${String(fleetId).trim()}|callcentre:${String(callcentreId).trim()}`;
            let hash = 0x811c9dc5;

            for (let index = 0; index < checksumInput.length; index++) {
                hash ^= checksumInput.charCodeAt(index);
                hash = Math.imul(hash, 0x01000193);
            }

            return ((hash >>> 0) % 46656)
                .toString(36)
                .padStart(3, '0')
                .toUpperCase();
        }

        /**
         * Checks whether a CVV is the expected value for a fleet and call-centre ID.
         */
        public static isFleetCvvValid(
            fleetId: string | number,
            callcentreId: string | number,
            cvv: string
        ): boolean {
            const normalizedCvv = String(cvv).trim().toUpperCase();

            return /^[A-Z0-9]{3}$/.test(normalizedCvv)
                && normalizedCvv === Functions.createFleetCvv(fleetId, callcentreId);
        }


        public static formatDuration(duration: number): string {
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            const seconds = duration % 60;

            return `${hours}h ${minutes}m ${seconds}s`;
        }

        public static formatDistance(distance: number): string {
            const kilometers = distance / 1000;
            return `${kilometers.toFixed(2)} km`;
        }

        /**
         * Calculates the minutes from now to the given future time.
         * @param varTime - The time in the FUTURE
         * @returns Minutes to time in future
         */
        public static calculateMinutes(varTime: Date) {
            
            //convert varTime to date object
            varTime = new Date(varTime);
            //convert the passed date to the same timezone we are in and get the difference in minutes from now
            const localTime = new Date(varTime.getTime() - varTime.getTimezoneOffset() * 60000);

            const now = new Date();
            const diff = localTime.getTime() - now.getTime();
            return Math.floor(diff / 60000); // Convert milliseconds to minutes
        }

        /**
         * Caculates the minutes since the given past time.
         * @param varTime  - The time in the PAST
         * @returns Minutes since time in past
         */
        public static calculateMinutesSince(varTime: Date) {
            //convert varTime to date object
            varTime = new Date(varTime);
            //convert the passed date to the same timezone we are in and get the difference in minutes from now
            const localTime = new Date(varTime.getTime() - varTime.getTimezoneOffset() * 60000);

            const now = new Date();
            const diff = now.getTime() - localTime.getTime();
            return Math.floor(diff / 60000); // Convert milliseconds to minutes
        }

        public static calculateTimeFromMinutes(minutes: number) {
            //Add minutes to current time and send back the new time
            const now = new Date();
            now.setMinutes(now.getMinutes() + minutes);
            return now;
        }

        /**
         * Formats minutes into a human-readable duration string
         * @param minutes - Number of minutes to format
         * @returns Formatted string like "2 days 3 hours 45 minutes"
         */
        public static formatMinutesToDuration(minutes: string | number): string {
            if (!minutes) {
                return '';
            }

            const totalMinutes = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
            
            if (isNaN(totalMinutes) || totalMinutes < 0) {
                return '';
            }

            const days = Math.floor(totalMinutes / (24 * 60));
            const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
            const mins = totalMinutes % 60;

            const timeString: string[] = [];

            if (days > 0) {
                timeString.push(`${days} day${days > 1 ? 's' : ''}`);
            }
            
            if (hours > 0) {
                timeString.push(`${hours} hour${hours > 1 ? 's' : ''}`);
            }
            
            if (mins > 0 || timeString.length === 0) {
                timeString.push(`${mins} minute${mins > 1 ? 's' : ''}`);
            }

            return timeString.join(' ');
        }

        public static normalizeSAPhoneNumber(phone: string): string {
            if (!phone) {
                return phone;
            }
            let normalized = phone.replace(/\s+/g, '');
            if (normalized.startsWith('+27')) {
                normalized = '0' + normalized.slice(3);
            } else if (normalized.startsWith('0027')) {
                normalized = '0' + normalized.slice(4);
            } else if (normalized.startsWith('27') && normalized.length === 11) {
                normalized = '0' + normalized.slice(2);
            }
            return normalized;
        }

        public static normalizeVehicleReg(value: string): string {
            return value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        }

        public static normalizePhoneDigits(value: string): string {
            return value.replace(/\D/g, '');
        }
    }
}
